import {
	getTransactionInterestText,
	getTransferInterestOptions,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionInterestText.ts';
import { getTransactionTypeText } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionTypeText.ts';
import { getTransactionErrorText } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionErrorText.ts';
import {
	TRANSACTION_TYPE_OPTIONS,
	getSecuritiesOptionsForSelect,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';
import { NONE_SPECIFIED_VALUE } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/utils.ts';
import {
	createTransactionActionColumn,
	createTransactionRestoreColumn,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/ButtonColumn';
import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { createTooltipColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TooltipColumn';
import { createUnitsColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/UnitsColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { ManageCapTransactionsTableColumn } from '@dealstack/shared/lib/common/business/cap/manageCap';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DATE_FORMATS, parseBEDateIgnoreTimezone } from '@dealstack/shared/lib/format/dates/date';
import { CurrencyType, DataSecurityTct, GetTransactionRes } from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

// CAP-1975: id of the legal/beneficial "Interest" column. Callers filter it out when the
// CAP_NOMINEE_STRUCTURES feature flag is off, keeping every nominee surface dark. CAP-2093: it is
// also the filter key, so it must match TransactionFilterDto.titles.
export const INTEREST_COLUMN_ID = ManageCapTransactionsTableColumn.titles;

export type TransactionCounts = {
	uniqueDateCount: number;
	uniqueSecurityCount: number;
	uniqueTypeCount: number;
	uniqueBuyerCount: number;
	uniqueSellerCount: number;
	uniqueBoughtForCount: number;
	uniqueSoldForCount: number;
	totalAmount: number;
	totalPrice: number;
};

export const getTransactionsTableColumns = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	entityCurrency?: CurrencyType | null,
	setShowSplitTransactionDrawer?: (value: boolean) => void,
	setEditTransaction?: (transaction: GetTransactionRes) => void,
	setShowEditTransactionDrawer?: (value: boolean) => void,
	setSplitTransaction?: (transaction: GetTransactionRes) => void,
	setShowPairTransactionDrawer?: (value: boolean) => void,
	setPairTransaction?: (transaction: GetTransactionRes) => void,
	setShowReorderTransactionDrawer?: (transaction: GetTransactionRes) => void,
	restoreTransaction?: (transactionId: string) => void,
	showActionColumn = false,
	showFooterColumns = false,
	transactionCounts?: TransactionCounts | null,
	securitiesNames?: DataSecurityTct[],
	entitiesSelectOptions?: { label: string; value: string }[],
	mipsSelectOptions?: { label: string; value: string }[],
	hasEditorPermission = true
): ExtendedColumnDef<GetTransactionRes>[] => {
	const entityOptionsWithNullOption = entitiesSelectOptions
		? [
				{
					label: tCapManage('select_options.none_specified'),
					value: NONE_SPECIFIED_VALUE,
				},
				...entitiesSelectOptions,
			]
		: [];
	const mipOptionsWithNullOption = mipsSelectOptions
		? [
				{
					label: tCapManage('select_options.none_specified'),
					value: NONE_SPECIFIED_VALUE,
				},
				...mipsSelectOptions,
			]
		: [];
	return [
		// @davidstellini 2024-02-02: Please do not delete - Uncomment to debug running balances
		// {
		// 	header: 'Source Balance',
		// 	accessorKey: ManageCapTransactionsTableColumn.sourceBalance,
		// 	enableColumnActions: false,
		// },
		// {
		// 	header: 'Dest Balance',
		// 	accessorKey: ManageCapTransactionsTableColumn.destBalance,
		// 	enableColumnActions: false,
		// },
		// {
		// 	header: 'Order',
		// 	accessorKey: ManageCapTransactionsTableColumn.order,
		// 	enableColumnActions: false,
		// },
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.id'),
			filterText: tCapManage('transactions.table.filter.id'),
			showFilter: true,
			accessorKey: ManageCapTransactionsTableColumn.id,
			columnLabel: tCapManage('transactions.table.columns.id'),
			filterValueType: 'uuid',
			minSize: 360,
			size: 360,
		}),
		createDateColumn({
			dateFormat: DATE_FORMATS.CALENDAR_DATE,
			headerText: tCapManage('transactions.table.columns.date'),
			columnLabel: tCapManage('transactions.table.columns.date'),
			filterText: tCapManage('transactions.table.filter.date'),
			showFilter: true,
			accessorKey: ManageCapTransactionsTableColumn.date,
			accessorFn: (row) => {
				if (!row.date) {
					return null;
				}
				return parseBEDateIgnoreTimezone(row.date);
			},
			filterVariant: 'date-range',
			filterValueType: 'date-range',
			showFooter: showFooterColumns,
			footerValue: transactionCounts?.uniqueDateCount,
			text: tCapManage('securities.table.columns.unique'),
		}),
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.security'),
			filterText: tCapManage('transactions.table.filter.security'),
			columnLabel: tCapManage('transactions.table.columns.security'),
			id: ManageCapTransactionsTableColumn.securityIds,
			accessorFn: (row) => row.securityName ?? LONG_DASH,
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: getSecuritiesOptionsForSelect(securitiesNames),
			},
			showFooter: true,
			showFilter: true,
			footerValue: transactionCounts?.uniqueSecurityCount,
			text: tCapManage('securities.table.columns.unique'),
			minSize: 300,
		}),
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.type'),
			columnLabel: tCapManage('transactions.table.columns.type'),
			id: ManageCapTransactionsTableColumn.types,
			accessorFn: (row) => getTransactionTypeText(row.type, tCapManage),
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: TRANSACTION_TYPE_OPTIONS,
			},
			showFooter: showFooterColumns,
			showFilter: true,
			footerValue: transactionCounts?.uniqueTypeCount,
			filterText: tCapManage('transactions.table.filter.type'),
			text: tCapManage('securities.table.columns.unique'),
		}),
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.seller'),
			columnLabel: tCapManage('transactions.table.columns.seller'),
			id: ManageCapTransactionsTableColumn.sourceIds,
			accessorFn: (row) => {
				return row.sourcePersonName ?? LONG_DASH;
			},
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: entitiesSelectOptions || [],
			},
			showFooter: showFooterColumns,
			showFilter: true,
			footerValue: transactionCounts?.uniqueSellerCount,
			filterText: tCapManage('transactions.table.filter.seller'),
			text: tCapManage('securities.table.columns.unique'),
		}),
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.seller_holding'),
			columnLabel: tCapManage('transactions.table.columns.seller_holding'),
			id: ManageCapTransactionsTableColumn.sellerMipIds,
			accessorFn: (row) => {
				return row.sellerMipName ?? LONG_DASH;
			},
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: mipOptionsWithNullOption,
			},
			showFooter: false,
			showFilter: true,
			filterText: tCapManage('transactions.table.filter.seller_holding'),
			enableHiding: true,
		}),
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.sold_for'),
			columnLabel: tCapManage('transactions.table.columns.sold_for'),
			id: ManageCapTransactionsTableColumn.soldForIds,
			accessorFn: (row) => {
				return row.soldForPersonName ?? LONG_DASH;
			},
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: entityOptionsWithNullOption,
			},
			showFooter: showFooterColumns,
			showFilter: true,
			footerValue: transactionCounts?.uniqueSoldForCount,
			filterText: tCapManage('transactions.table.filter.sold_for'),
			text: tCapManage('securities.table.columns.unique'),
		}),
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.buyer'),
			columnLabel: tCapManage('transactions.table.columns.buyer'),
			id: ManageCapTransactionsTableColumn.destinationIds,
			accessorFn: (row) => {
				return row.destinationPersonName ?? LONG_DASH;
			},
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: entitiesSelectOptions || [],
			},
			showFooter: showFooterColumns,
			showFilter: true,
			footerValue: transactionCounts?.uniqueBuyerCount,
			filterText: tCapManage('transactions.table.filter.buyer'),
			text: tCapManage('securities.table.columns.unique'),
		}),
		// CAP-1975: legal/beneficial interest carried by this transaction (nominee structures).
		// CAP-2093: filters server-side on `title`; still not sortable.
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.interest'),
			columnLabel: tCapManage('transactions.table.columns.interest'),
			filterText: tCapManage('transactions.table.filter.interest'),
			id: INTEREST_COLUMN_ID,
			accessorFn: (row) => getTransactionInterestText(row.title, tCapManage) ?? LONG_DASH,
			enableSorting: false,
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: getTransferInterestOptions(tCapManage),
			},
			showFilter: true,
			showFooter: false,
			enableHiding: true,
		}),
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.buyer_holding'),
			columnLabel: tCapManage('transactions.table.columns.buyer_holding'),
			id: ManageCapTransactionsTableColumn.buyerMipIds,
			accessorFn: (row) => {
				return row.buyerMipName ?? LONG_DASH;
			},
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: mipOptionsWithNullOption,
			},
			showFooter: false,
			showFilter: true,
			filterText: tCapManage('transactions.table.filter.buyer_holding'),
			enableHiding: true,
		}),
		createTextColumn({
			headerText: tCapManage('transactions.table.columns.bought_for'),
			columnLabel: tCapManage('transactions.table.columns.bought_for'),
			id: ManageCapTransactionsTableColumn.boughtForIds,
			accessorFn: (row) => {
				return row.boughtForPersonName ?? LONG_DASH;
			},
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: entityOptionsWithNullOption,
			},
			showFooter: showFooterColumns,
			showFilter: true,
			footerValue: transactionCounts?.uniqueBoughtForCount,
			filterText: tCapManage('transactions.table.filter.bought_for'),
			text: tCapManage('securities.table.columns.unique'),
		}),
		createUnitsColumn({
			headerText: tCapManage('transactions.table.columns.number_of_units'),
			filterText: tCapManage('transactions.table.filter.number_of_units'),
			columnLabel: tCapManage('transactions.table.columns.number_of_units'),
			accessorKey: ManageCapTransactionsTableColumn.amount,
			showFilter: true,
			accessorFn: (row) => {
				return !row.units || row.units === 0 ? LONG_DASH : Number(row.units);
			},
			showFooter: showFooterColumns,
			footerValue: transactionCounts?.totalAmount,
			text: tCapManage('securities.table.columns.total'),
		}),
		createNumberColumn({
			headerText: `${tCapManage('transactions.table.columns.transaction_cost')} (${entityCurrency ?? ''})`,
			filterText: tCapManage('transactions.table.filter.transaction_cost'),
			columnLabel: tCapManage('transactions.table.columns.transaction_cost'),
			accessorKey: ManageCapTransactionsTableColumn.price,
			lockPrecision: true,
			showFilter: true,
			accessorFn: (row) => {
				return {
					value: row.cost,
				};
			},
			showFooter: showFooterColumns,
			footerValue: transactionCounts?.totalPrice,
			text: tCapManage('securities.table.columns.total'),
		}),
		createTooltipColumn({
			header: '',
			columnLabel: tCapManage('transactions.table.columns.transaction_error'),
			accessorKey: ManageCapTransactionsTableColumn.transactionError,
			enableColumnFilter: false,
			showFilter: true,
			size: 40,
			meta: {
				isGrow: false,
			},
			// Validity has nothing to do with whether row actions are shown. Gating this on
			// showActionColumn meant the reorder modal rendered no warning at all, on exactly the
			// rows a reorder is meant to fix. A deleted row is excluded from the balance fold, so
			// it has no verdict to report — every one would read "no source balance".
			accessorFn: (row) =>
				row.deleted ? null : getTransactionErrorText(row.transactionError, tCapManage),
		}),
		...(!showActionColumn && restoreTransaction
			? [createTransactionRestoreColumn<GetTransactionRes>(tCapManage, restoreTransaction)]
			: []),
		...(showActionColumn && hasEditorPermission
			? [
					createTransactionActionColumn<GetTransactionRes>(
						tCapManage,
						setShowEditTransactionDrawer,
						setShowPairTransactionDrawer,
						setShowSplitTransactionDrawer,
						setShowReorderTransactionDrawer,
						setEditTransaction,
						setSplitTransaction,
						setPairTransaction,
						(row) => (row.transactionsOnSameDateCount ?? 0) > 1
					),
				]
			: []),
	];
};
